import { ComponentProps, ReactNode, RefObject, type JSX } from 'react';
import NextLink from 'next/link';
import Typography from '@alltrails/core/components/Typography';
import type Context from '@alltrails/core/types/Context';
import type { UserId } from '@alltrails/core/types/Context';
import type User from '@alltrails/core/types/User';
import { IconDefinition } from './components/IconRenderer';

/**
 * Creates a union type of the specified size options, restricting the allowed values for consistency
 */
export type Size<K extends 'xs' | 'sm' | 'md' | 'lg' | 'xl'> = K;

export type ColorBadge = 'accent' | 'default' | 'inverse';

/**
 * Props that should be used by any component that passes through to InputField.
 * labelText is a string when the label is hidden because it should be applied as an aria-label.
 */
export type InputBaseProps = {
  disabled?: boolean;
  errorText?: ReactNode;
  helperText?: ReactNode;
} & (
  | {
      hideLabel?: false;
      labelText?: ReactNode;
      labelTextVariant?: ComponentProps<typeof Typography>['variant'];
    }
  | {
      hideLabel?: true;
      labelText?: string;
      labelTextVariant?: never;
    }
);

/**
 * https://developer.mozilla.org/en-US/docs/Web/HTML/Element/a#attr-target
 */
export type LinkTarget = '_blank' | '_self' | '_parent' | '_top';

/**
 * https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/rel
 */
export type LinkRel =
  | 'alternate'
  | 'author'
  | 'bookmark'
  | 'external'
  | 'help'
  | 'license'
  | 'next'
  | 'nofollow'
  | 'noopener'
  | 'noreferrer'
  | 'opener'
  | 'prev'
  | 'search'
  | 'tag';

/**
 * Convenience type for components that accept links
 */
export type LinkInfo = {
  href: string;
  target?: LinkTarget;
  rel?: LinkRel[];
  outsideOfMugen?: boolean;
  prefetch?: ComponentProps<typeof NextLink>['prefetch'];
};

export type DropdownMenuTextOption = {
  className?: string;
  leadingIcon?: {
    background?: boolean;
    customElement?: JSX.Element;
    icon?: IconDefinition<'color' | 'orientation'>;
  };
  linkInfo?: LinkInfo;
  onClick?: (e?: React.MouseEvent<HTMLElement>) => void;
  selected?: boolean;
  subtext?: ReactNode;
  testId: string;
  title: ReactNode;
  trailingIcon?: {
    customElement?: JSX.Element;
    icon?: IconDefinition<'color' | 'orientation' | 'size'>;
  };
};

export type DropdownMenuSectionOption = {
  sectionHeader: ReactNode;
};

export type DropdownMenuOption = DropdownMenuTextOption | DropdownMenuSectionOption | JSX.Element;

export type PopupSelectOption = {
  id: number;
  name: string;
  parentId?: number;
  isSelected?: boolean | 'indeterminate';
  isExpanded?: boolean;
  isDeleted?: boolean;
  onClick?: () => void;
};

export type PopupSelectOptions = Map<string, PopupSelectOption>;

export type PopupSelectBaseProps = {
  options: PopupSelectOptions;
  searchTerm?: string;
  selectedIds?: string[];
  setCurrentOptions: (options: PopupSelectOptions) => void;
};

export type PopupSelectOptionsProps = PopupSelectBaseProps & {
  containerRef?: RefObject<HTMLDivElement>;
};

export type LightboxDropdownMenuState = { isOpen: boolean; options: DropdownMenuOption[] };

export enum ActionEnumLinks {
  LINK_DELETE_PHOTO_COMPLETELY = 1,
  LINK_REMOVE_PHOTO_FROM_MAP = 2,
  LINK_SUGGEST_DELETE_FROM_TRAIL = 3,
  LINK_SET_AS_DEFAULT_MAP_PHOTO = 4,
  LINK_SET_AS_DEFAULT_TRAIL_PHOTO = 5,
  // Currently unused
  // LINK_SUGGEST_AS_DEFAULT_TRAIL_PHOTO = 6,
  LINK_SET_AS_DEFAULT_LIST_PHOTO = 7,
  LINK_REMOVE_PHOTO_FROM_TRAIL = 8
}

export type ActionPhotoLink = {
  actionEnum: ActionEnumLinks;
  href: string;
  label: string;
};

export type SuccessToast = {
  isVisible: boolean;
  text: string;
};

type CreditTitle = {
  title: string;
  user_name?: string;
  user_profile_url?: string;
  attribution?: string;
};

type Photo = {
  id: number;
  user_id: UserId;
  trail_id?: number;
  caption: string;
  image: {
    url: string;
  };
  title?: string;
};

export type LightboxPhoto = {
  photo?: Photo;
  url: string;
  creditTitle?: CreditTitle;
  timestamp?: string;
  trailSlug?: string;
  includeMapId?: string;
  includeListId?: string;
  firstLink?: ActionPhotoLink;
  secondLink?: ActionPhotoLink;
  thirdLink?: ActionPhotoLink;
  photoUnhideLink?: ActionPhotoLink;
};

type PhotoUser = {
  id: number | string;
  firstName: string;
  lastName: string;
};

export type LightboxItem = {
  id?: number | string;
  url?: string;
  imgUrl?: string;
  created_at?: string;
  description?: string;
  caption?: string;
  user?: PhotoUser;
  photoScore?: string | number;
};

export type LightboxUser = {
  id: UserId;
  userName: string;
  pro?: boolean;
  profileUrl: string;
};

export type PhotoType = 'map' | 'trail' | 'list';

export type LightboxDropdownHandlers = {
  onDeletePhotoClick: () => void;
  onRemovePhotoClick: (type: PhotoType) => void;
  onSuggestHideClick: () => void;
  onSetAsHighlightClick: (type: PhotoType) => void;
};

export type CreditLinkResponse = {
  context: Context;
  creditTitle: CreditTitle;
  firstLink: ActionPhotoLink;
  includeListId?: string;
  includeMapId?: string;
  photo: Photo;
  url: string;
  photoTrailUrl?: string;
  photoUnhideLink?: ActionPhotoLink;
  secondLink?: ActionPhotoLink;
  thirdLink?: ActionPhotoLink;
  showAction: boolean;
  timestamp: string;
  trailSlug: string;
};

type PhotoLocation = {
  postalCode?: number;
  city?: string;
  region?: string;
  country?: string;
  latitude?: number;
  longitude?: number;
};

export type PhotosResponse = {
  dataUid: string | null;
  description: string | null;
  id: number;
  likeCount: number;
  location: PhotoLocation;
  metadata: {
    created: string;
    updated: string;
    status: string;
  };
  photoHash: string;
  title: string;
  trailId: number | null;
  trailIds: number[];
  user: User;
};

type Meta = { status: string };

export type MetaResponse = {
  meta: Meta;
};
